import React, { useState, useEffect } from 'react';
import { taxApi, projectApi } from '../services/api';
import { Card, Button, Select, Spinner, Badge } from '../components/UI';
import { FilePdf, ChartPie, ArrowUp, ArrowDown, Scales } from '@phosphor-icons/react';
import jsPDF from 'jspdf';
import { autoTable } from 'jspdf-autotable';
import { fmtCurrency } from '../utils/currencyFormat';
import { useBranding } from '../context/BrandingContext';

export default function VATReportPage() {
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [report, setReport] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [loading, setLoading] = useState(true);
  const branding = useBranding();

  useEffect(() => { projectApi.list().then(r => setProjects(r.data)).finally(() => setLoading(false)); }, []);

  useEffect(() => { loadReport(); }, [selectedProject, startDate, endDate]);

  const loadReport = async () => {
    try {
      const params = {};
      if (selectedProject) params.project_id = selectedProject;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      const [reportRes, ledgerRes] = await Promise.all([
        taxApi.vatReport(params),
        taxApi.ledger(params),
      ]);
      setReport(reportRes.data);
      setLedger(ledgerRes.data);
    } catch {}
  };

  const fmt = (n) => fmtCurrency(n);

  const exportPDF = () => {
    if (!report) return;
    const doc = new jsPDF();
    const now = new Date().toLocaleString('en-US');

    doc.setFontSize(16);
    doc.text('VAT Return Report', 14, 20);
    doc.setFontSize(9);
    doc.setTextColor(100);
    if (report.trn) doc.text(`TRN: ${report.trn}`, 14, 28);
    const periodText = startDate || endDate ? `Period: ${startDate || 'Start'} to ${endDate || 'Present'}` : 'Period: All Time';
    doc.text(periodText, 14, report.trn ? 34 : 28);
    doc.text(`Generated: ${now}`, 14, report.trn ? 40 : 34);

    let startY = report.trn ? 48 : 42;

    // Output Tax section
    doc.setFontSize(11);
    doc.setTextColor(0);
    doc.text('Output Tax (Sales)', 14, startY);
    startY += 3;

    const outputRows = report.output_tax.breakdown.map(b => [
      b.tax_name, `${b.tax_percentage}%`, fmt(b.taxable_amount), fmt(b.tax_amount), String(b.count)
    ]);
    outputRows.push(['', '', fmt(report.output_tax.total_taxable_sales), fmt(report.output_tax.total_tax), '']);

    autoTable(doc, {
      startY,
      head: [['Tax Rate', '%', 'Taxable Sales', 'Output VAT', 'Count']],
      body: outputRows.length > 1 ? outputRows : [['No output tax records', '', '', '', '']],
      styles: { fontSize: 8 },
      margin: { bottom: 30 },
    });

    startY = doc.lastAutoTable.finalY + 10;

    // Input Tax section
    doc.setFontSize(11);
    doc.text('Input Tax (Purchases)', 14, startY);
    startY += 3;

    const inputRows = report.input_tax.breakdown.map(b => [
      b.tax_name, `${b.tax_percentage}%`, fmt(b.taxable_amount), fmt(b.tax_amount), String(b.count)
    ]);
    inputRows.push(['', '', fmt(report.input_tax.total_taxable_purchases), fmt(report.input_tax.total_tax), '']);

    autoTable(doc, {
      startY,
      head: [['Tax Rate', '%', 'Taxable Purchases', 'Input VAT', 'Count']],
      body: inputRows.length > 1 ? inputRows : [['No input tax records', '', '', '', '']],
      styles: { fontSize: 8 },
      margin: { bottom: 30 },
    });

    startY = doc.lastAutoTable.finalY + 10;

    // Summary
    doc.setFontSize(11);
    doc.text('VAT Summary', 14, startY);
    startY += 3;

    autoTable(doc, {
      startY,
      head: [['Item', 'Amount']],
      body: [
        ['Total Output VAT (Collected)', fmt(report.output_tax.total_tax)],
        ['Total Input VAT (Paid)', fmt(report.input_tax.total_tax)],
        ['Net VAT Payable', fmt(report.net_vat_payable)],
      ],
      styles: { fontSize: 9 },
      margin: { bottom: 30 },
    });

    // Footer branding
    const pageCount = doc.internal.getNumberOfPages();
    const devLine = branding.developer_tagline
      ? `Developed by ${branding.developer_name}  |  ${branding.developer_tagline}`
      : `Developed by ${branding.developer_name}`;
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      const pageH = doc.internal.pageSize.getHeight();
      const pageW = doc.internal.pageSize.getWidth();
      doc.setDrawColor(200, 200, 200);
      doc.line(14, pageH - 25, pageW - 14, pageH - 25);
      doc.setFontSize(6.5);
      doc.setTextColor(160, 160, 160);
      doc.text('This report is system-generated and subject to verification. The system provider is not liable for decisions made based on this data.', 14, pageH - 20);
      doc.setFontSize(7);
      doc.text(devLine, 14, pageH - 14);
      doc.text(`${branding.company_name}  |  Generated: ${now}`, 14, pageH - 9);
      doc.text(`Page ${i} of ${pageCount}`, pageW - 14, pageH - 9, { align: 'right' });
    }

    doc.save('VAT_Return_Report.pdf');
  };

  if (loading) return <div className="flex justify-center py-8"><Spinner size="lg" /></div>;

  return (
    <div className="space-y-4" data-testid="vat-report-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#0A0A0A]">VAT Report</h1>
          <p className="text-sm text-[#666]">Tax return summary — output VAT, input VAT, net payable</p>
        </div>
        <Button onClick={exportPDF} disabled={!report} data-testid="export-vat-pdf">
          <FilePdf className="w-4 h-4 mr-2" /> Export PDF
        </Button>
      </div>

      {report?.trn && (
        <div className="text-xs text-[#888]" data-testid="vat-trn">TRN: <strong className="text-[#333]">{report.trn}</strong></div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Select value={selectedProject} onChange={(e) => setSelectedProject(e.target.value)} data-testid="vat-filter-project">
          <option value="">All Projects</option>
          {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </Select>
        <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
          className="px-3 py-2 border border-[#E0E0E0] rounded-md text-sm" data-testid="vat-start-date" />
        <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
          className="px-3 py-2 border border-[#E0E0E0] rounded-md text-sm" data-testid="vat-end-date" />
      </div>

      {report && (
        <>
          {/* Summary KPIs */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="vat-kpis">
            <Card className="p-3 text-center">
              <p className="text-[10px] text-[#999] uppercase">Taxable Sales</p>
              <p className="text-lg font-bold text-[#002FA7]">{fmt(report.output_tax.total_taxable_sales)}</p>
            </Card>
            <Card className="p-3 text-center">
              <div className="flex items-center justify-center gap-1">
                <ArrowUp className="w-3.5 h-3.5 text-[#FF3333]" />
                <p className="text-[10px] text-[#999] uppercase">Output VAT</p>
              </div>
              <p className="text-lg font-bold text-[#FF3333]">{fmt(report.output_tax.total_tax)}</p>
            </Card>
            <Card className="p-3 text-center">
              <div className="flex items-center justify-center gap-1">
                <ArrowDown className="w-3.5 h-3.5 text-[#00C853]" />
                <p className="text-[10px] text-[#999] uppercase">Input VAT</p>
              </div>
              <p className="text-lg font-bold text-[#00C853]">{fmt(report.input_tax.total_tax)}</p>
            </Card>
            <Card className="p-3 text-center">
              <div className="flex items-center justify-center gap-1">
                <Scales className="w-3.5 h-3.5 text-[#6B7280]" />
                <p className="text-[10px] text-[#999] uppercase">Net Payable</p>
              </div>
              <p className={`text-lg font-bold ${report.net_vat_payable >= 0 ? 'text-[#FF3333]' : 'text-[#00C853]'}`}>
                {fmt(report.net_vat_payable)}
              </p>
            </Card>
          </div>

          {/* Output Tax Breakdown */}
          <Card className="p-4" data-testid="output-tax-breakdown">
            <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
              <ArrowUp className="w-4 h-4 text-[#FF3333]" /> Output Tax (Sales)
            </h3>
            {report.output_tax.breakdown.length === 0 ? (
              <p className="text-sm text-[#888]">No output tax recorded</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-[10px] text-[#999] uppercase border-b">
                    <th className="pb-2">Tax Rate</th><th className="pb-2 text-right">Taxable Sales</th><th className="pb-2 text-right">VAT Amount</th><th className="pb-2 text-right">Invoices</th>
                  </tr></thead>
                  <tbody>
                    {report.output_tax.breakdown.map((b, i) => (
                      <tr key={i} className="border-b border-[#F0F0F0]">
                        <td className="py-2">{b.tax_name} ({b.tax_percentage}%)</td>
                        <td className="py-2 text-right">{fmt(b.taxable_amount)}</td>
                        <td className="py-2 text-right font-bold text-[#FF3333]">{fmt(b.tax_amount)}</td>
                        <td className="py-2 text-right text-[#666]">{b.count}</td>
                      </tr>
                    ))}
                    <tr className="font-bold border-t-2 border-[#E0E0E0]">
                      <td className="py-2">Total</td>
                      <td className="py-2 text-right">{fmt(report.output_tax.total_taxable_sales)}</td>
                      <td className="py-2 text-right text-[#FF3333]">{fmt(report.output_tax.total_tax)}</td>
                      <td></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          {/* Input Tax Breakdown */}
          <Card className="p-4" data-testid="input-tax-breakdown">
            <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
              <ArrowDown className="w-4 h-4 text-[#00C853]" /> Input Tax (Purchases)
            </h3>
            {report.input_tax.breakdown.length === 0 ? (
              <p className="text-sm text-[#888]">No input tax recorded</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-[10px] text-[#999] uppercase border-b">
                    <th className="pb-2">Tax Rate</th><th className="pb-2 text-right">Taxable Purchases</th><th className="pb-2 text-right">VAT Amount</th><th className="pb-2 text-right">Expenses</th>
                  </tr></thead>
                  <tbody>
                    {report.input_tax.breakdown.map((b, i) => (
                      <tr key={i} className="border-b border-[#F0F0F0]">
                        <td className="py-2">{b.tax_name} ({b.tax_percentage}%)</td>
                        <td className="py-2 text-right">{fmt(b.taxable_amount)}</td>
                        <td className="py-2 text-right font-bold text-[#00C853]">{fmt(b.tax_amount)}</td>
                        <td className="py-2 text-right text-[#666]">{b.count}</td>
                      </tr>
                    ))}
                    <tr className="font-bold border-t-2 border-[#E0E0E0]">
                      <td className="py-2">Total</td>
                      <td className="py-2 text-right">{fmt(report.input_tax.total_taxable_purchases)}</td>
                      <td className="py-2 text-right text-[#00C853]">{fmt(report.input_tax.total_tax)}</td>
                      <td></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          {/* Tax Ledger */}
          <Card className="p-4" data-testid="tax-ledger-table">
            <h3 className="text-sm font-semibold mb-3">Tax Ledger ({ledger.length} entries)</h3>
            {ledger.length === 0 ? (
              <p className="text-sm text-[#888]">No tax transactions recorded</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-[10px] text-[#999] uppercase border-b">
                    <th className="pb-2">Date</th><th className="pb-2">Direction</th><th className="pb-2">Reference</th><th className="pb-2">Tax Rate</th>
                    <th className="pb-2 text-right">Taxable</th><th className="pb-2 text-right">Tax</th>
                  </tr></thead>
                  <tbody>
                    {ledger.map((entry, i) => (
                      <tr key={entry.id || i} className="border-b border-[#F0F0F0]">
                        <td className="py-2 text-[#666]">{entry.created_at ? new Date(entry.created_at).toLocaleDateString('en-US') : '-'}</td>
                        <td className="py-2">
                          <Badge variant={entry.tax_direction === 'output' ? 'danger' : 'success'}>
                            {entry.tax_direction === 'output' ? 'Output' : 'Input'}
                          </Badge>
                        </td>
                        <td className="py-2">{entry.ref_number || entry.ref_type}</td>
                        <td className="py-2 text-[#666]">{entry.tax_name} ({entry.tax_percentage}%)</td>
                        <td className="py-2 text-right">{fmt(entry.taxable_amount)}</td>
                        <td className={`py-2 text-right font-bold ${entry.tax_direction === 'output' ? 'text-[#FF3333]' : 'text-[#00C853]'}`}>
                          {fmt(entry.tax_amount)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
}
